/**
 * Shared constants for candidate-related forms and display.
 * PARTY_OPTIONS is derived from the canonical registry in ./parties.ts.
 */

export { PARTY_DISPLAY_OPTIONS as PARTY_OPTIONS } from './parties';

/** Offices that require a district number (state House/Senate + U.S. House). */
/** Offices we import from TRACER (must match ALLOWED_OFFICES in the import service, title-cased). */
export const OFFICE_OPTIONS = [
  'Colorado House',
  'Colorado House Vacancy',
  'Colorado Senate',
  'Colorado Senate Vacancy',
  'U.S. House',
  'U.S. Senate',
  'Governor',
  'Lt. Governor',
  'Attorney General',
  'Secretary of State',
  'State Treasurer',
];

/** Map admin office label → `electoral_districts.scope` for dropdown lookup (Colorado v1). */
export function electoralDistrictScopeForOffice(officeSought: string): 'co_state_house' | 'co_state_senate' | 'us_house_co' | null {
  const t = (officeSought || '').trim();
  if (!t) return null;
  /** Matches directory-style chamber label until DB is normalized to "Colorado House". */
  if (t === 'House') return 'co_state_house';
  if (t.includes('Colorado House')) return 'co_state_house';
  if (t.includes('Colorado Senate')) return 'co_state_senate';
  if (t === 'U.S. House') return 'us_house_co';
  return null;
}

/**
 * Map an admin office label → `offices.office_key` (the contest model's key).
 * Mirrors `officeKeyForOfficeSought` in backend/src/services/candidacyContestService.js —
 * keep the two in sync. Used to narrow the contest picker to the chosen office.
 */
export function officeKeyForOfficeSought(officeSought: string): string | null {
  const compact = (officeSought || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!compact) return null;
  if (compact.includes('president')) return 'us_president';
  if (compact.includes('lieutenantgovernor') || compact.includes('lt.governor') || compact.includes('ltgovernor')) return 'lt_governor';
  if (compact.includes('governor')) return 'governor';
  if (compact.includes('u.s.senate') || compact.includes('ussenate') || compact.includes('unitedstatessenate')) return 'us_senate';
  if (compact.includes('u.s.house') || compact.includes('ushouse') || compact.includes('congress')) return 'us_house';
  if (compact.includes('secretaryofstate')) return 'secretary_of_state';
  if (compact.includes('attorneygeneral')) return 'attorney_general';
  if (compact.includes('treasurer')) return 'treasurer';
  if (compact.includes('coloradosenate') || compact.includes('statesenate')) return 'co_state_senate';
  if (
    compact.includes('coloradohouse') || compact.includes('statehouse')
    || compact.includes('staterepresentative') || compact.includes('coloradorepresentative')
  ) return 'co_state_house';
  return null;
}

/** U.S. House / U.S. Senate use federal jurisdiction in admin flows. */
export function isFederalCongressionalOffice(officeSought: string): boolean {
  const t = (officeSought || '').trim();
  return t === 'U.S. House' || t === 'U.S. Senate';
}

export const ELECTION_TYPES = [
  { value: 'primary', label: 'Primary' },
  { value: 'general', label: 'General' },
  { value: 'special', label: 'Special' },
  { value: 'runoff', label: 'Runoff' },
];

/** Default Colorado election dates (YYYY-MM-DD for `<input type="date">`). Sync with `backend/src/constants/coloradoElectionDefaults.js`. */
/** Current calendar year start date (`YYYY-01-01`) for admin date-input defaults. */
export function defaultCurrentYearStartDate(): string {
  const y = new Date().getFullYear();
  return `${y}-01-01`;
}

/** When DB has no affidavit date, prefill `<input type="date">` with Jan 1 of the current calendar year. */
function withDefaultAffidavitFiledDate<T extends { affidavit_filed_date?: string | null }>(row: T): T {
  const out = { ...row };
  const a = out.affidavit_filed_date;
  if (a == null || (typeof a === 'string' && a.trim() === '')) {
    (out as { affidavit_filed_date?: string }).affidavit_filed_date = defaultCurrentYearStartDate();
  }
  return out;
}

/** Missing committee registered date defaults to Jan 1 of current year for admin edit forms. */
export function defaultCommitteeDateRegistered(v: string | null | undefined): string {
  const raw = typeof v === 'string' ? v.trim() : '';
  return raw || defaultCurrentYearStartDate();
}

/** Canonical `candidates.sos_jurisdiction` values (admin + TRACER). Append when adding County, Federal, etc. */
export const DEFAULT_SOS_JURISDICTION = 'STATEWIDE' as const;

export const SOS_JURISDICTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'STATEWIDE', label: 'Statewide' },
  { value: 'FEDERAL', label: 'Federal' },
];

export const SOS_JURISDICTION_VALUES = new Set(SOS_JURISDICTION_OPTIONS.map(o => o.value));

/** Missing/blank → STATEWIDE for the admin edit form (Cancel still restores raw server row). */
export function defaultSosJurisdiction(v: string | null | undefined): string {
  const t = typeof v === 'string' ? v.trim() : '';
  return t || DEFAULT_SOS_JURISDICTION;
}

/** UI label for a stored jurisdiction code; unknown non-empty values pass through. */
export function jurisdictionLabel(value: string | null | undefined): string {
  const t = typeof value === 'string' ? value.trim() : '';
  if (!t) {
    const fb = SOS_JURISDICTION_OPTIONS.find(o => o.value === DEFAULT_SOS_JURISDICTION);
    return fb?.label ?? DEFAULT_SOS_JURISDICTION;
  }
  const found = SOS_JURISDICTION_OPTIONS.find(o => o.value === t);
  return found?.label ?? t;
}

function withFormJurisdictionDefault<T extends { sos_jurisdiction?: string | null }>(row: T): T {
  return { ...row, sos_jurisdiction: defaultSosJurisdiction(row.sos_jurisdiction) };
}

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'defeated', label: 'Defeated' },
  { value: 'elected', label: 'Elected' },
  { value: 'suspended', label: 'Suspended (Inactive)' },
  { value: 'pending', label: 'Pending' },
];

export const STATUS_VALUES = new Set(STATUS_OPTIONS.map(s => s.value));

/**
 * Same vocabulary as `candidates.status` / API validation — used for SOS admin mirror fields
 * (`candidate_status_sos`, `campaign_status_sos`) and finance committee `status` (not TRACER CSV).
 */
export const DEFAULT_ADMIN_LIFECYCLE_STATUS = 'active' as const;

/** Blank → active; known values normalized to lowercase slug; other strings preserved for legacy rows. */
export function defaultAdminLifecycleStatus(v: string | null | undefined): string {
  const raw = typeof v === 'string' ? v.trim() : '';
  if (!raw) return DEFAULT_ADMIN_LIFECYCLE_STATUS;
  const lower = raw.toLowerCase();
  const known = STATUS_OPTIONS.find(s => s.value === lower);
  if (known) return known.value;
  return raw;
}

/** Label for read-only display; blank → Active; unknown raw string unchanged. */
export function adminLifecycleStatusLabel(value: string | null | undefined): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) {
    const fb = STATUS_OPTIONS.find(s => s.value === DEFAULT_ADMIN_LIFECYCLE_STATUS);
    return fb?.label ?? DEFAULT_ADMIN_LIFECYCLE_STATUS;
  }
  const lower = raw.toLowerCase();
  const opt = STATUS_OPTIONS.find(s => s.value === lower);
  return opt?.label ?? raw;
}

function withFormAdminLifecycleStatusDefaults<
  T extends { candidate_status_sos?: string | null; campaign_status_sos?: string | null },
>(row: T): T {
  return {
    ...row,
    candidate_status_sos: defaultAdminLifecycleStatus(row.candidate_status_sos),
    campaign_status_sos: defaultAdminLifecycleStatus(row.campaign_status_sos),
  };
}

/** Admin candidate detail: jurisdiction + SOS status mirror fields default for the edit form. */
export function withAdminCandidateFormDefaults<
  T extends {
    sos_jurisdiction?: string | null;
    candidate_status_sos?: string | null;
    campaign_status_sos?: string | null;
    affidavit_filed_date?: string | null;
  },
>(row: T): T {
  return withDefaultAffidavitFiledDate(
    withFormAdminLifecycleStatusDefaults(withFormJurisdictionDefault(row)),
  );
}

export const ROLE_TYPES = [
  { value: 'campaign_manager', label: 'Campaign Manager' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'deputy_manager', label: 'Deputy Manager' },
  { value: 'finance_director', label: 'Finance Director' },
  { value: 'communications_director', label: 'Communications Director' },
  { value: 'field_director', label: 'Field Director' },
  { value: 'volunteer_coordinator', label: 'Volunteer Coordinator' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'other', label: 'Other' },
];

export const COMPENSATION_TYPES = [
  { value: 'salary', label: 'Salary' },
  { value: 'contract', label: 'Contract' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'commission', label: 'Commission' },
  { value: 'volunteer', label: 'Volunteer' },
];

/** person_contact_methods.context for finance committee agents (admin committee card). */
export const FINANCE_COMMITTEE_CONTACT_CONTEXT = {
  REGISTERED_AGENT: 'finance_committee_registered_agent',
  DESIGNATED_FILING: 'finance_committee_designated_filing_agent',
} as const;

/**
 * Default `campaign_finance_committees.committee_type` on the admin candidate detail page.
 * Matches SOS / campaign finance labeling; if the linked row has another type, it stays shown and editable.
 */
export const DEFAULT_CANDIDATE_COMMITTEE_TYPE = 'Candidate Committee';

/** Use default when stored type is missing or whitespace-only. */
export function committeeTypeOrDefault(stored: string | null | undefined): string {
  const t = stored?.trim();
  return t || DEFAULT_CANDIDATE_COMMITTEE_TYPE;
}

/** Map status value → Chip variant. */
export function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (status) {
    case 'active': return 'success';
    case 'elected': return 'info';
    case 'withdrawn': return 'warning';
    case 'defeated': return 'danger';
    case 'suspended': return 'neutral';
    default: return 'neutral';
  }
}
